import type { ReactNode } from "react"

import type {
  SegmentNewsItem,
  SegmentRecommendedAction,
  SegmentSourceTier,
  SegmentStrategyMatrix,
} from "../types"

import { ChevronRight, Link as LinkIcon, Newspaper } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { CountUp } from "./count-up"
import { safeHref } from "../utils/safe-href"

/** Normalize a recommended action (string | {text, ref}) to its parts. */
function actionParts(action: SegmentRecommendedAction): { text: string; ref?: string } {
  return typeof action === "string" ? { text: action } : { text: action.text, ref: action.ref }
}

function ActionRefChip({
  refName,
  onRefClick,
}: {
  refName: string
  onRefClick?: (companyName: string) => void
}) {
  if (onRefClick) {
    return (
      <Button
        className="text-primary hover:bg-primary/10 hover:text-primary ml-2 h-auto gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold"
        variant="ghost"
        onClick={() => onRefClick(refName)}
      >
        {refName}
        <ChevronRight className="size-3" />
      </Button>
    )
  }
  return (
    <span className="bg-primary/10 text-primary ml-2 rounded-md px-1.5 py-0.5 text-xs font-semibold">
      {refName}
    </span>
  )
}

export function SectionCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <article className="border-border bg-card rounded-lg border p-4 shadow-sm">
      <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-[0.12em] uppercase">
        {title}
      </h3>
      {children}
    </article>
  )
}

export function ActionCard({
  active,
  items,
  title,
  onRefClick,
}: {
  active: boolean
  items: SegmentRecommendedAction[]
  title: string
  /** Drill into the customer/competitor a recommended action references (§ deep-links). */
  onRefClick?: (companyName: string) => void
}) {
  return (
    <article
      className={`rounded-lg border p-4 shadow-sm ${
        active ? "border-primary/25 bg-primary/10" : "border-border bg-card"
      }`}
    >
      <h3 className="text-foreground mb-3 text-sm font-semibold">{title}</h3>
      {items.length === 0 ? (
        <EmptyTabText>Run a full report to populate.</EmptyTabText>
      ) : (
        <ul className="text-muted-foreground grid gap-2 text-sm">
          {items.map((action, index) => {
            const { text, ref } = actionParts(action)
            return (
              <li key={`${text}-${index}`} className="pl-3 before:mr-2 before:content-['-']">
                {text}
                {ref ? <ActionRefChip refName={ref} onRefClick={onRefClick} /> : null}
              </li>
            )
          })}
        </ul>
      )}
    </article>
  )
}

export function KeyValue({
  confidence,
  label,
  value,
}: {
  confidence?: "low" | "medium" | "high"
  label: string
  value: string
}) {
  return (
    <div className="border-border flex items-start justify-between gap-4 border-b py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-foreground text-right font-medium tabular-nums">
          <CountUp value={value} />
        </span>
        {confidence ? <ConfidenceBadge confidence={confidence} /> : null}
      </span>
    </div>
  )
}

export function ConfidenceBadge({ confidence }: { confidence: "low" | "medium" | "high" }) {
  const styles: Record<string, string> = {
    high: "bg-success/15 text-success",
    medium: "bg-warning/15 text-warning-foreground",
    low: "bg-destructive/15 text-destructive",
  }
  return (
    <span
      title={`Confidence: ${confidence}`}
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase",
        styles[confidence]
      )}
    >
      {confidence[0]}
    </span>
  )
}

/**
 * Per-source trust chip (10x M3.3). `trusted` gets a subtle positive chip, `low`
 * a warning chip; `standard` renders nothing to avoid badging every ordinary link.
 */
export function SourceTierBadge({ tier }: { tier: SegmentSourceTier }) {
  if (tier === "standard") return null
  const styles: Record<string, string> = {
    trusted: "bg-success/15 text-success",
    low: "bg-warning/15 text-warning-foreground",
  }
  const label = tier === "trusted" ? "Trusted" : "Low trust"
  return (
    <span
      title={`Source trust: ${tier}`}
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold",
        styles[tier]
      )}
    >
      {label}
    </span>
  )
}

export function SourceLinks({
  sources,
  confidence,
  trustByUrl,
}: {
  sources: string[]
  /** Confidence in the figure these sources back — badged next to the label (§5.6). */
  confidence?: "low" | "medium" | "high"
  /** Per-URL trust tier (report.grounded.sourceTrustByUrl) — badges each link (10x M3.3). */
  trustByUrl?: Record<string, SegmentSourceTier>
}) {
  const links = sources.filter((source) => safeHref(source) !== "")
  if (links.length === 0) return null
  const unique = Array.from(new Set(links)).slice(0, 5)
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground text-[10px] font-semibold tracking-[0.08em] uppercase">
        Sources
      </span>
      {confidence ? <ConfidenceBadge confidence={confidence} /> : null}
      {unique.map((url, index) => (
        <span key={url} className="inline-flex items-center gap-1">
          <a
            className="text-primary inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
            href={url}
            rel="noreferrer"
            target="_blank"
          >
            <LinkIcon className="size-3" />
            {hostname(url) || `Source ${index + 1}`}
          </a>
          {trustByUrl?.[url] ? <SourceTierBadge tier={trustByUrl[url]} /> : null}
        </span>
      ))}
    </div>
  )
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return ""
  }
}

export function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return <EmptyTabText>Run a full report to populate.</EmptyTabText>

  return (
    <ul className="text-muted-foreground grid gap-2 text-sm">
      {items.map((item) => (
        <li key={item} className="pl-3 before:mr-2 before:content-['-']">
          {item}
        </li>
      ))}
    </ul>
  )
}

export function EmptyTabText({ children }: { children: ReactNode }) {
  return <p className="bg-muted/50 text-muted-foreground rounded-md p-3 text-sm">{children}</p>
}

/**
 * Dated market-news feed with per-item source (§5.2). Items keep their
 * confidence in the data, but the list shows no confidence badge (#3340 /
 * Nolato feedback #33).
 */
export function NewsFeed({
  items,
  hasReport = false,
}: {
  items: SegmentNewsItem[]
  hasReport?: boolean
}) {
  if (items.length === 0) {
    // After a run, an empty feed means nothing was published inside the
    // 90-day window (#3340) — not that a report is missing.
    return (
      <EmptyTabText>
        {hasReport
          ? "No market news from the last 90 days."
          : "Run a full report to surface recent market news."}
      </EmptyTabText>
    )
  }
  return (
    <ul className="grid gap-3">
      {items.map((item, index) => (
        <li key={`${item.headline}-${index}`} className="bg-muted/50 rounded-md p-3">
          <h4 className="text-foreground flex items-start gap-2 text-sm font-semibold">
            <Newspaper className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
            {item.headline}
          </h4>
          {item.summary ? (
            <p className="text-muted-foreground mt-1 text-sm">{item.summary}</p>
          ) : null}
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {item.date ? <span>{item.date}</span> : null}
            <NewsSource item={item} />
          </div>
        </li>
      ))}
    </ul>
  )
}

function NewsSource({ item }: { item: SegmentNewsItem }) {
  if (item.url) {
    return (
      <a
        className="text-primary inline-flex items-center gap-1 underline-offset-2 hover:underline"
        href={safeHref(item.url)}
        rel="noreferrer"
        target="_blank"
      >
        <LinkIcon className="size-3" />
        {item.source || hostname(item.url) || "Source"}
      </a>
    )
  }
  if (item.source) return <span>{item.source}</span>
  return null
}

/** TOWS strategy conclusions grid derived from the SWOT (§ SWOT). */
export function TowsMatrix({ matrix }: { matrix: SegmentStrategyMatrix }) {
  const quadrants: Array<{ key: keyof SegmentStrategyMatrix; title: string; hint: string }> = [
    { key: "so", title: "SO — Attack", hint: "Strengths × Opportunities" },
    { key: "st", title: "ST — Defend", hint: "Strengths × Threats" },
    { key: "wo", title: "WO — Improve", hint: "Weaknesses × Opportunities" },
    { key: "wt", title: "WT — Avoid", hint: "Weaknesses × Threats" },
  ]
  const total = quadrants.reduce((sum, q) => sum + matrix[q.key].length, 0)
  if (total === 0) {
    return <EmptyTabText>Run a full report to derive TOWS strategy conclusions.</EmptyTabText>
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {quadrants.map((q) => (
        <article key={q.key} className="border-border bg-muted/40 rounded-lg border p-4">
          <h4 className="text-foreground text-sm font-semibold">{q.title}</h4>
          <p className="text-muted-foreground/70 mb-2 text-[10px] tracking-[0.08em] uppercase">
            {q.hint}
          </p>
          <BulletList items={matrix[q.key]} />
        </article>
      ))}
    </div>
  )
}

export function Divider() {
  return <div className="bg-border my-4 h-px" />
}
